import { apiClient } from '_app'
import { ApiResponse, ReportDto } from 'dtos'
import { CustomerExportReportFormDto } from 'dtos/customerExportReportFormDto'
import { FinancialTransactionAddedAfterInvoicingReportDto } from 'dtos/financialTransactionAddedAfterInvoicingReportDto'
import { PaginationParameters } from 'parameters'
import { download } from 'utils'

export const getCustomerExportReportForm = () =>
  apiClient
    .get<ApiResponse<CustomerExportReportFormDto>>('/api/reports/customer-export-report/form')
    .then(response => response.data)

export const getReports = () => apiClient.get<ApiResponse<ReportDto[]>>('/api/reports').then(response => response.data)

export const getAvailableReports = () => apiClient.get<ApiResponse<ReportDto[]>>('/api/reports/available').then(response => response.data)

export const downloadCustomerExportReport = (companyGuid: string, companyName: string) =>
  apiClient
    .get<Blob>(`/api/reports/customer-export-report?companyGuid=${encodeURIComponent(companyGuid)}`, {
      responseType: 'blob'
    })
    .then(res => {
      download(res.data, getCustomerExportReportFileName(companyName), 'text/csv')
    })

const getCustomerExportReportFileName = (companyName: string) => {
  const date = new Date()
  const timestamp = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('')
  const safeCompanyName = companyName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')

  return `${safeCompanyName || 'Customer'}ExportReport_${timestamp}.csv`
}

export interface FinancialTransactionReportFilters {
  transactionDateFrom?: string
  transactionDateTo?: string
  importerStockNumber?: string
  chargeTypeId?: number
  year?: number
  make?: string
  model?: string
  vin?: string
}

export interface FinancialTransactionReportLookupFilters {
  lookupField: 'importerStockNumber' | 'vin' | 'year' | 'make' | 'model'
  lookupSearch?: string
  year?: number
  make?: string
  model?: string
}

export type FinancialTransactionReportParameters = FinancialTransactionReportFilters & PaginationParameters

const financialTransactionReportQuery = (parameters: Partial<FinancialTransactionReportParameters>) => {
  const query = new URLSearchParams()

  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value))
    }
  })

  return query.toString()
}

export const getFinancialTransactionsAddedAfterInvoicing = (parameters: FinancialTransactionReportParameters) =>
  apiClient
    .get<ApiResponse<FinancialTransactionAddedAfterInvoicingReportDto[]>>(
      `/api/reports/financial-transactions-added-after-invoicing?${financialTransactionReportQuery(parameters)}`
    )
    .then(response => response.data)

export const getFinancialTransactionReportLookup = (parameters: FinancialTransactionReportLookupFilters) =>
  apiClient
    .get<ApiResponse<{ values: string[] }>>(
      `/api/reports/financial-transactions-added-after-invoicing/filter-options?${financialTransactionReportQuery(parameters)}`
    )
    .then(response => response.data)

export const downloadFinancialTransactionsAddedAfterInvoicing = (parameters: FinancialTransactionReportParameters) =>
  apiClient
    .get<Blob>(
      `/api/reports/financial-transactions-added-after-invoicing.csv?${financialTransactionReportQuery(parameters)}`,
      { responseType: 'blob' }
    )
    .then(response => {
      const date = new Date()
      const timestamp = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
        '_',
        String(date.getHours()).padStart(2, '0'),
        String(date.getMinutes()).padStart(2, '0'),
        String(date.getSeconds()).padStart(2, '0')
      ].join('')

      download(response.data, `FinancialTransactionsAddedAfterInvoicing_${timestamp}.csv`, 'text/csv')
    })
